/**
 * The live star layer: every catalogue star (sky/starfield.ts) is a pixel-sized Gaussian billboard at infinity,
 * so stars stay crisp and stable at every preset and resolution, dim behind the baked dust lanes, and are hidden
 * by ships and bodies in front of them (depth-tested, never depth-writing). One draw call.
 */
import {
  AdditiveBlending,
  BufferAttribute,
  InstancedBufferAttribute,
  InstancedBufferGeometry,
  Mesh,
  MeshBasicNodeMaterial,
  type CubeTexture,
  type UniformNode,
} from 'three/webgpu'
import {
  cameraProjectionMatrix,
  cubeTexture,
  exp,
  float,
  instancedBufferAttribute,
  length,
  modelViewMatrix,
  positionGeometry,
  uniform,
  uv,
  varying,
  vec3,
  vec4,
  viewportSize,
} from 'three/tsl'
import type { StarField } from './starfield'

/** Quad side in units of the star's FWHM: covers the Gaussian out to about 1.3% of its peak. */
export const STAR_QUAD_FWHM = 2.5

export interface StarLayer {
  readonly mesh: Mesh
  readonly brightness: UniformNode<'float', number>
  /** Swaps in another catalogue of the same size (a new system seed, M3 R3): uploads in place, no new buffers. */
  setField(field: StarField): void
  dispose(): void
}

export function createStarLayer(field: StarField, radius: number, dust: CubeTexture, dustDimming: number): StarLayer {
  const geo = new InstancedBufferGeometry()
  geo.setAttribute('position', new BufferAttribute(new Float32Array([-0.5, -0.5, 0, 0.5, -0.5, 0, 0.5, 0.5, 0, -0.5, 0.5, 0]), 3))
  geo.setAttribute('uv', new BufferAttribute(new Float32Array([0, 0, 1, 0, 1, 1, 0, 1]), 2))
  geo.setIndex([0, 1, 2, 0, 2, 3])
  const aDir = new InstancedBufferAttribute(field.dir, 3)
  const aSize = new InstancedBufferAttribute(field.size, 1)
  const aColor = new InstancedBufferAttribute(field.color, 3)
  geo.setAttribute('aStarDir', aDir)
  geo.setAttribute('aStarSize', aSize)
  geo.setAttribute('aStarColor', aColor)
  geo.instanceCount = field.count

  const dir = instancedBufferAttribute<'vec3'>(aDir, 'vec3')
  const size = instancedBufferAttribute<'float'>(aSize, 'float')
  const col = instancedBufferAttribute<'vec3'>(aColor, 'vec3')
  const uBrightness = uniform(1)

  const m = new MeshBasicNodeMaterial({ transparent: true, depthWrite: false, blending: AdditiveBlending })
  // At infinity: rotate only (w = 0), then push out to the star sphere radius.
  const viewDir = modelViewMatrix.mul(vec4(dir, 0)).xyz
  const clip = cameraProjectionMatrix.mul(vec4(viewDir.mul(radius), 1))
  const quadPx = size.mul(STAR_QUAD_FWHM)
  const offset = positionGeometry.xy.mul(quadPx).mul(2).div(viewportSize).mul(clip.w)
  m.vertexNode = vec4(clip.xy.add(offset), clip.z, clip.w)
  // Dust lanes in the bake (alpha = extinction) dim the stars behind them. Sampled per star in the vertex stage.
  const extinction = cubeTexture(dust, dir).level(float(0)).a
  const transmit = varying(float(1).sub(extinction.mul(dustDimming)), 'vStarTransmit')
  const px = length(uv().sub(0.5)).mul(varying(quadPx, 'vStarQuadPx'))
  const sigma = varying(size, 'vStarFwhm').div(2.3548)
  const g = exp(px.mul(px).div(sigma.mul(sigma).mul(-2)))
  m.colorNode = vec3(varying(col, 'vStarColor')).mul(g).mul(transmit).mul(uBrightness)

  const mesh = new Mesh(geo, m)
  mesh.name = 'sky:stars'
  mesh.frustumCulled = false
  mesh.renderOrder = -1
  return {
    mesh,
    brightness: uBrightness,
    setField(next: StarField): void {
      if (next.count !== field.count) throw new Error(`star catalogue size changed (${field.count} → ${next.count})`)
      field.dir.set(next.dir)
      field.size.set(next.size)
      field.color.set(next.color)
      aDir.needsUpdate = true
      aSize.needsUpdate = true
      aColor.needsUpdate = true
    },
    dispose(): void {
      geo.dispose()
      m.dispose()
    },
  }
}
